import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

/**
 * Generic CRUD editor for client-area collections: products, campaigns, documents,
 * financials, timeline. All work with:
 *   GET/POST/PUT/DELETE  /admin/{path}  and  /admin/{path}/{id}
 *
 * Props:
 *   title, subtitle, path (e.g. "client-products"),
 *   fields: [{ key, label, type, options?, placeholder?, span? }]  where
 *          type ∈ text|number|textarea|date|select|clientSelect|productSelect|campaignSelect|boolean
 *   columns: [{ key, label, render? }]
 *   defaults: object with default new-item values
 */
export default function GenericAdminCRUD({ title, subtitle, path, fields, columns, defaults = {} }) {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);

  const load = () => {
    const params = {};
    if (filterClient) params.client_id = filterClient;
    if (filterProduct) params.product_id = filterProduct;
    api.get(`/admin/${path}`, { params }).then(({ data }) => setItems(data));
  };

  useEffect(() => {
    api.get("/admin/clients-auth").then(({ data }) => setClients(data));
    api.get("/admin/client-products").then(({ data }) => setProducts(data));
    api.get("/admin/client-campaigns").then(({ data }) => setCampaigns(data));
  }, []);
  useEffect(() => { load(); }, [filterClient, filterProduct]);

  const submit = async () => {
    try {
      const payload = { ...defaults, ...form };
      if (editing) await api.put(`/admin/${path}/${editing.id}`, payload);
      else await api.post(`/admin/${path}`, payload);
      toast.success(editing ? "Atualizado" : "Criado");
      setForm({}); setEditing(null); load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const startEdit = (it) => { setEditing(it); setForm({ ...it }); };
  const cancel = () => { setEditing(null); setForm({}); };
  const del = async (it) => {
    if (!window.confirm("Excluir este item?")) return;
    await api.delete(`/admin/${path}/${it.id}`);
    load();
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-4xl">{title}</h1>
        {subtitle && <p className="text-sm text-neutral-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={filterClient} onChange={(e) => { setFilterClient(e.target.value); setFilterProduct(""); }} className="border border-black/15 px-3 py-2 rounded text-sm">
          <option value="">Todos os clientes</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.company || c.name || c.email}</option>)}
        </select>
        <select value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} className="border border-black/15 px-3 py-2 rounded text-sm">
          <option value="">Todos os produtos</option>
          {products.filter((p) => !filterClient || p.client_id === filterClient).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <section className="border border-black/10 rounded-md p-5 mb-6">
        <div className="font-display text-xl mb-4">{editing ? "Editando item" : "Novo item"}</div>
        <div className="grid md:grid-cols-2 gap-3">
          {fields.map((f) => {
            let inp;
            if (f.type === "textarea") inp = <textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={3} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />;
            else if (f.type === "select") inp = (
              <select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm">
                <option value="">—</option>
                {(f.options || []).map((o) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
              </select>
            );
            else if (f.type === "clientSelect") inp = (
              <select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm">
                <option value="">Selecione…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.company || c.email}</option>)}
              </select>
            );
            else if (f.type === "productSelect") inp = (
              <select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm">
                <option value="">—</option>
                {products.filter((p) => !form.client_id || p.client_id === form.client_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            );
            else if (f.type === "campaignSelect") inp = (
              <select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm">
                <option value="">—</option>
                {campaigns.filter((c) => (!form.client_id || c.client_id === form.client_id) && (!form.product_id || c.product_id === form.product_id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            );
            else if (f.type === "boolean") inp = (
              <select value={form[f.key] ? "1" : "0"} onChange={(e) => setForm({ ...form, [f.key]: e.target.value === "1" })} className="w-full border border-black/15 px-3 py-2 rounded text-sm">
                <option value="0">Não</option><option value="1">Sim</option>
              </select>
            );
            else if (f.type === "number") inp = <input type="number" step="any" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value === "" ? "" : Number(e.target.value) })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />;
            else if (f.type === "date") inp = <input type="date" value={(form[f.key] || "").slice(0, 10)} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />;
            else inp = <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || ""} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />;
            return (
              <div key={f.key} className={f.span === 2 ? "md:col-span-2" : ""}>
                <label className="text-xs text-neutral-500">{f.label}</label>
                {inp}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={submit} className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange"><Save className="w-3 h-3 inline mr-1" /> {editing ? "Atualizar" : "Criar"}</button>
          {editing && <button onClick={cancel} className="text-sm px-4 py-2.5 rounded-full border border-black/15">Cancelar</button>}
        </div>
      </section>

      <div className="border border-black/10 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black text-white text-xs uppercase tracking-wider">
            <tr>{columns.map((c) => <th key={c.key} className="p-3 text-left">{c.label}</th>)}<th></th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-black/5 hover:bg-black/[0.02]">
                {columns.map((c) => (
                  <td key={c.key} className="p-3">{c.render ? c.render(it, { clients, products, campaigns }) : (it[c.key] ?? "—")}</td>
                ))}
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(it)} className="text-xs text-black hover:text-accent-orange">Editar</button>
                  <button onClick={() => del(it)} className="text-xs text-red-500 hover:text-red-700 ml-3"><Trash2 className="w-3 h-3 inline" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="p-8 text-center text-neutral-500">Nenhum item.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
